import { filter, map, pairwise, timeout } from 'rxjs/operators';
import { Injector, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { UPDATES_TYPES } from '@shared/constants/updates-types.const';
import { APP_INFO } from '@shared/constants/app-info.const';
import { DbDaoService } from '../db/db-dao.service';
import { LoadingService } from './loading.service';
import { LocalStorage } from '../utils/localstorage.utils';
import { SecuredResponse } from '@shared/classes/secured-response.class';
import { UuidService } from '../utils/uuid.utils';
import { Employee } from '@shared/classes/employee.class';
import { SERVER_CONFIG } from '@shared/constants/server.const';
import { License } from '@shared/classes/license.class';
import { TranslateService } from '@ngx-translate/core';
import { RequestsWbService } from './requests-wb.service';
import { Company } from '@shared/classes/company.class';
import { Image, IMAGE_SIZES } from '@shared/classes/image.class';
import { BehaviorSubject, firstValueFrom, Observable, Subject, TimeoutError } from 'rxjs';
import { AlertService } from './alert.service';
import { ConvertDataForRequest } from '@shared/decorators/convert-data.decor';
import { CoreFields, LogService, headersForLogs } from './logger';
import { PosSettingsService } from './pos-settings.service';
import { PAYMASH_PROFILE } from '@profile';
import { RequestHeaderTypes } from '@shared/constants/request-headers.enum';
import { StorageKeys } from '@shared/constants/storage.const';
import { FileReaderUtils } from '../utils/file-reader/file-reader.utils';
import { SERVER_RESPONSE_ERRORS } from '@shared/constants/server-response-errors.const';
import { ISecureRequestOptions, ServerError } from '@shared/interfaces';
import { CompanyProperties, TargetDeviceEnum } from '@shared/constants';
import { FingerprintPlugin } from '@paymash/capacitor-fingerprint-plugin';
import { ErrorLevel, LogParams } from '@paymash/logging';
import { Store } from '@shared/classes/store.class';
import { PosInfoService } from '@core/services/system/pos-info.service';
import { SecureStorageWrapperService } from './secure-storadge/secure-storage-wrapper.service';

export interface LoggedUserDataInterface {
  accessToken: string;
  refreshToken: string;
  pos: { channelId: number };
  company: Company;
  user: Employee;
}

@Injectable()
export class SecurityService {
  public loggedUserData: LoggedUserDataInterface = null;
  public loggedCompanyData: BehaviorSubject<Company> = new BehaviorSubject(null);
  public deviceIdentifier: string;
  public deviceFingerprint: string;
  public activeStore: Store = null;
  public readonly badRefreshToken = new Subject<{ clearLocalDatabase: boolean; isExpiredVersion?: boolean }>();
  // TODO ADJUST LOGIC OF REPEATED SUCCESS PAGE LOAD
  public isSuccessPageActive: boolean = false;
  private readonly _activeStoreUpdate = signal<Store | null | undefined>(undefined, { equal: () => false });
  public readonly activeStoreUpdate = this._activeStoreUpdate.asReadonly();
  public readonly logoutEvent = new Subject<void>();
  private errorAlert: any;
  private companyLogo: string;
  private storeList$ = new BehaviorSubject<Store[]>([]);
  public readonly resyncEvent = new Subject<void>();
  private readonly logger = this.logService.createLogger('SecurityService');
  private pendingRefresh: Promise<HttpResponse<any>> | null = null;

  // Expected (self-healing / non-actionable) HTTP failures — logged quietly so they stay out of
  // error logs and Sentry; any status not listed here is logged as an error. This map is the single
  // knob for tuning which statuses stay out of prod and at which level (e.g. add `503: warning`).
  private readonly expectedRequestErrors: Record<number, (message: string, meta: LogParams) => void> = {
    429: (message, meta) => this.logger.trace(message, meta), // rate-limited — retried below via Retry-After
  };

  constructor(
    public http: HttpClient,
    private inject: Injector,
    private LocalStorage: LocalStorage,
    private LoadingService: LoadingService,
    private UuidService: UuidService,
    private DbDaoService: DbDaoService,
    private TranslateService: TranslateService,
    private AlertService: AlertService,
    public RequestsWbService: RequestsWbService,
    private posSettingsService: PosSettingsService,
    private logService: LogService,
    private secureStorageWrapperService: SecureStorageWrapperService
  ) {}

  public setLoggedUserData(data: LoggedUserDataInterface) {
    if (data !== null) {
      this.posSettingsService.setCompanyUuid(data.company.uuid);
      this.LocalStorage.setObject(StorageKeys.loggedUserData, data);
      try {
        this.logService.setSentryTag(CoreFields.CompanyUuid, data.company.uuid);
        this.logService.setSentryTag(CoreFields.CompanyId, data.company.id.toString());
        this.logService.setField(CoreFields.CompanyUuid, data.company.uuid);
        this.logService.setField(CoreFields.CompanyId, data.company.id.toString());
      } catch (err) {
        this.logger.error(err, `setLoggedUserData:logService.companyUuid`);
      }
    } else {
      this.posSettingsService.setCompanyUuid(null);
      this.LocalStorage.remove(StorageKeys.loggedUserData);
      this.logService.setSentryTag(CoreFields.CompanyUuid, undefined);
      this.logService.setSentryTag(CoreFields.CompanyId, undefined);
      this.logService.setField(CoreFields.CompanyUuid, null);
      this.logService.setField(CoreFields.CompanyId, null);
    }
    this.loggedUserData = data;
  }

  public async getCompanyLogoBase64(companyData: Company): Promise<string> {
    if (!companyData.image) {
      throw new Error('No company image');
    }
    const image: Image = new Image(companyData.image);
    const imageURL: string = image.getImageUrlBySize(IMAGE_SIZES.NORMAL);
    try {
      const blob = await firstValueFrom(this.http.get(imageURL, { responseType: 'blob' }));
      return new Promise<string>((resolve, reject) => {
        const reader = FileReaderUtils.getFileReader();
        reader.onload = (event) => resolve(event.target['result'] as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      this.logger.error(err, 'getCompanyLogoBase64');
      throw err;
    }
  }

  private removeCompanyLogo() {
    this.LocalStorage.remove(StorageKeys.companyLogo);
    this.companyLogo = null;
  }

  public saveCompanyLogo(data) {
    this.LocalStorage.set(StorageKeys.companyLogo, data);
    this.companyLogo = data;
  }

  public getCompanyLogo(): string {
    if (!this.companyLogo) {
      this.companyLogo = this.LocalStorage.get(StorageKeys.companyLogo);
    }
    return this.companyLogo && this.companyLogo.indexOf('base64') > -1 && this.companyLogo.split('base64,')[1];
  }

  public getLoggedUserData() {
    return this.loggedUserData;
  }

  public getDeviceIdentifier() {
    const key = StorageKeys.deviceIdentifier;
    const targetDevice: TargetDeviceEnum = this.LocalStorage.get(StorageKeys.targetDevice) as TargetDeviceEnum;
    const isCanUseSecureStorage =
      [TargetDeviceEnum.ANDROID, TargetDeviceEnum.IOS].includes(targetDevice) && this.secureStorageWrapperService?.isPluginWasInitialed;

    if (this.deviceIdentifier) return this.deviceIdentifier;

    const deviceIdentifier =
      isCanUseSecureStorage && this.secureStorageWrapperService.get(key)
        ? this.secureStorageWrapperService.get(key)
        : this.LocalStorage.get(key);

    if (deviceIdentifier) {
      this.deviceIdentifier = deviceIdentifier as string;
    } else {
      this.deviceIdentifier = this.UuidService.generate();
      if (isCanUseSecureStorage) {
        this.secureStorageWrapperService.set(key, this.deviceIdentifier);
      } else {
        this.LocalStorage.set(key, this.deviceIdentifier);
      }
    }
    return this.deviceIdentifier;
  }

  public async setDeviceFingerprint() {
    if (this.deviceFingerprint) {
      return;
    }

    let deviceFingerprint = this.LocalStorage.get(StorageKeys.deviceFingerprint);
    if (!deviceFingerprint) {
      deviceFingerprint = await this.getFingerprint();
      this.LocalStorage.set(StorageKeys.deviceFingerprint, deviceFingerprint);
    }
    this.deviceFingerprint = deviceFingerprint;
  }

  public getDeviceFingerprint() {
    return this.deviceFingerprint;
  }

  getNextInvoiceNumber(): number {
    const nextInvoiceNumber = this.LocalStorage.get('nextInvoiceNumber');
    return nextInvoiceNumber ? parseInt(nextInvoiceNumber) : 1;
  }

  increaseNextInvoiceNumber() {
    this.LocalStorage.set('nextInvoiceNumber', (this.getNextInvoiceNumber() + 1).toString());
  }

  decreaseNextInvoiceNumber() {
    const nextInvoiceNumber = this.getNextInvoiceNumber();
    if (nextInvoiceNumber > 1) {
      this.LocalStorage.set('nextInvoiceNumber', (nextInvoiceNumber - 1).toString());
    }
  }

  public setActiveStore(storeData: Store) {
    const companyUuid: string = this.loggedCompanyData.getValue()?.uuid;
    this.activeStore = storeData ? new Store(storeData) : null;
    if (companyUuid) {
      this.LocalStorage.set(`${StorageKeys.activeStore}${companyUuid}`, JSON.stringify(this.activeStore));
      this._activeStoreUpdate.set(storeData);
    }
  }

  public getActiveStore(): Store {
    return this.activeStore;
  }

  public setStoreList(stores: Store[]) {
    this.storeList$.next(stores);
  }

  public getStoreList() {
    return this.storeList$;
  }

  public setLoggedCompanyData(companyData: any): void {
    this.loggedCompanyData.next(new Company(companyData));
    if (companyData !== null) {
      this.getCompanyLogoBase64(companyData)
        .then((data) => {
          this.saveCompanyLogo(data);
        })
        .catch(() => {
          this.removeCompanyLogo();
        });
    }

    const savedStoreData = this.LocalStorage.get(`${StorageKeys.activeStore}${this.loggedCompanyData.getValue()?.uuid}`);
    if (savedStoreData && savedStoreData !== 'null') {
      this.activeStore = new Store(JSON.parse(savedStoreData));
    }
  }

  public getLoggedCompanyData(): Company {
    return this.loggedCompanyData.getValue();
  }

  public setMigratedStatus(isMigrated: boolean): void {
    let company: Company = this.loggedCompanyData.getValue();
    if (company.isMigrated !== isMigrated) {
      company.isMigrated = isMigrated;
      this.setLoggedCompanyData(company);
    }
  }

  public createAuthorizationHeader() {
    let headers = new HttpHeaders();
    const { PosId, RequestId, AppVersion, Authorization, CompanyId, Target, DeviceFingerprint } = RequestHeaderTypes;
    const loggedCompanyData = this.loggedCompanyData.getValue();
    headers = headers.append(PosId, this.getDeviceIdentifier());
    headers = headers.append(RequestId, this.UuidService.generate());
    headers = headers.append(AppVersion, APP_INFO.APP_VERSION);
    headers = headers.append(DeviceFingerprint, this.getDeviceFingerprint());
    if (this.loggedUserData && this.loggedUserData.accessToken) {
      headers = headers.append(Authorization, `Bearer ${this.loggedUserData.accessToken}`);
      if (loggedCompanyData) {
        headers = headers.append(CompanyId, loggedCompanyData.uuid);
      }
    }
    if (loggedCompanyData && loggedCompanyData.isMigrated) {
      headers = headers.append(Target, 'paymash2');
    }
    return headers;
  }

  public loadViaWorker(config: any): Promise<any> {
    return new Promise((resolve, reject) => {
      let id: any, msgFn: any;
      msgFn = (e: any) => {
        if (e && e.data && (e.data.status === 200 || e.data.status === 201)) {
          resolve(e.data);
        } else {
          reject(e.data);
        }
        this.RequestsWbService.terminate(id);
      };

      id = this.RequestsWbService.load(config, msgFn, reject);
    });
  }

  @ConvertDataForRequest()
  public async doSecureRequest(url: string, type: string, data?: Object, optionsData?: ISecureRequestOptions): Promise<SecuredResponse> {
    const dataToSend = data;
    const useHttp = optionsData?.useHttp ?? false;
    const showLoading = optionsData?.showLoading ?? false;
    let requestTimeout = optionsData?.timeout;

    if (showLoading) this.LoadingService.showLoadingItem();
    this.LoadingService.syncStart();
    const headers = this.createAuthorizationHeader();
    const options: any = {
      headers,
      observe: 'response',
    };
    let reresponseType: string = '';
    if (optionsData?.responseTypeData) {
      options.responseType = optionsData.responseTypeData;
      reresponseType = optionsData.responseTypeData.toLowerCase();
    }
    if (optionsData?.body) {
      options.body = optionsData.body;
    }

    let request: Promise<HttpResponse<any>>;
    const posInfo: PosInfoService = this.inject.get(PosInfoService);
    const { platform, iOSVersion } = posInfo.getDeviceFullInfoSnapshot();
    if (
      !RequestsWbService.supported ||
      (window.cordova && platform.toLowerCase() === 'ios' && iOSVersion.toString().split('.')[0] === '9') ||
      useHttp
    ) {
      request = this.createRequestAsPromise(url, type, options, dataToSend, requestTimeout);
    } else {
      requestTimeout = requestTimeout || PAYMASH_PROFILE.requestTimeout.normal;
      const config: any = {
        method: type,
        url: url,
        data: data,
        options: options,
        headers: this.createAuthorizationHeader(),
        responseType: reresponseType,
        timeout: requestTimeout,
      };
      request = this.loadViaWorker(config);
    }

    let requestId;
    const startTime = performance.now();
    try {
      requestId = headers.get(RequestHeaderTypes.RequestId);
      this.logger.trace(`Begin ${type} ${url}`, { requestId });
    } catch (error) {
      this.logger.error(error, 'doSecureRequest');
    }

    try {
      const responseData: HttpResponse<any> = await request;
      try {
        const requestDuration = performance.now() - startTime;
        this.logger.trace(`Completed ${type} ${url}`, {
          requestId,
          requestDuration: requestDuration.toFixed(2),
          requestStatus: responseData.status,
        });
      } catch (error) {
        this.logger.error(error, 'doSecureRequest');
      }
      if (showLoading) this.LoadingService.hideLoadingItem();
      this.LoadingService.syncFinish();
      return new SecuredResponse(responseData);
    } catch (errorResponse) {
      let errorData: HttpResponse<any> = errorResponse;
      if (errorData instanceof TimeoutError) {
        errorData = this.createTimeoutErrorResponse(url);
      }
      try {
        const requestDuration = performance.now() - startTime;
        const requestMeta = { requestId, requestDuration: requestDuration.toFixed(2), requestStatus: errorData.status };
        this.logRequestError(errorData, `Completed ${type} ${url}`, requestMeta);
      } catch (error) {
        this.logger.error(error, 'doSecureRequest');
      }

      let parsedData;
      this.LoadingService.syncFinish();
      try {
        parsedData = errorData['error'];
      } catch (err) {}

      if (
        errorData.status === 401 &&
        parsedData &&
        parsedData.type !== 'Authentication' &&
        parsedData.type !== 'AccessToken' &&
        this.loggedUserData &&
        this.loggedUserData.refreshToken
      ) {
        try {
          const refreshedData: HttpResponse<any> = await this.refreshToken(url, type, dataToSend);
          if (showLoading) this.LoadingService.hideLoadingItem();
          return new SecuredResponse(refreshedData);
        } catch (refreshError) {
          if (showLoading) this.LoadingService.hideLoadingItem();
          throw new SecuredResponse(refreshError as HttpResponse<any>);
        }
      }

      if (errorData.status === 402) {
        const badRefreshTokenMessage = { clearLocalDatabase: false };
        this.badRefreshToken.next(badRefreshTokenMessage);
      }
      if (showLoading) this.LoadingService.hideLoadingItem();
      if (errorData.status === 429) {
        const retryAfter = parseInt(errorData.headers.get(RequestHeaderTypes.RetryAfter)) || PAYMASH_PROFILE.requestTimeout.retryAfter;
        await new Promise((r) => setTimeout(r, retryAfter * 1000));
      }
      throw new SecuredResponse(errorData);
    }
  }

  /**
   * Logs the outcome of a failed HTTP request. Statuses in {@link expectedRequestErrors} are
   * treated as expected / self-healing (e.g. 429 rate-limit, retried via Retry-After) and logged
   * quietly so they stay out of error logs and Sentry; every other status is logged as an error.
   * Adjust that map to change which statuses reach prod and at which level.
   */
  private logRequestError(errorData: HttpResponse<any>, message: string, meta: LogParams) {
    const logExpected = this.expectedRequestErrors[errorData.status];
    if (logExpected) {
      logExpected(message, meta);
      return;
    }
    this.logger.error(errorData, message, { level: ErrorLevel.Medium }, meta);
  }

  public async refreshToken(url: string, type: string, dataToSend: Object): Promise<HttpResponse<any>> {
    const options = {
      headers: this.createAuthorizationHeader(),
      observe: 'response',
    };
    const tokenData = {
      refreshToken: this.loggedUserData?.refreshToken,
      deviceIdentifier: this.getDeviceIdentifier(),
      deviceFingerprint: this.getDeviceFingerprint(),
    };
    const { normal } = PAYMASH_PROFILE.requestTimeout;
    this.logger.debug('refreshToken:createRequestAsPromise:data', { headers: headersForLogs(options.headers) });

    let isRefreshOwner = false;
    let isRetryRequest = false;
    if (!this.pendingRefresh) {
      isRefreshOwner = true;
      this.pendingRefresh = this.createRequestAsPromise(
        `${SERVER_CONFIG.API_URL}authorization/accessToken`,
        'post',
        options,
        tokenData,
        normal
      );
    }

    try {
      const data: HttpResponse<any> = await this.pendingRefresh;
      const newLoggedUserData = { ...this.loggedUserData };
      newLoggedUserData.accessToken = data.body['properties']['accessToken'];
      this.setLoggedUserData(newLoggedUserData);
      if (url) {
        const newHeaders = this.createAuthorizationHeader();
        isRetryRequest = true;
        return await this.createRequestAsPromise(url, type, { headers: newHeaders, observe: 'response' }, dataToSend);
      }
      return data;
    } catch (errorData) {
      let data: HttpResponse<any> = errorData;
      this.logger.error(data, 'refreshToken:createRequestAsPromise', { level: ErrorLevel.Medium });
      if (data instanceof TimeoutError) {
        data = this.createTimeoutErrorResponse(url);
      }
      if ((data && (data.status === 400 || data.status === 401)) || data.status === 402) {
        // A failed retry is the original request's own error (e.g. 400 INVOICE_IS_ALREADY_DELETED), not a bad refresh token.
        if (isRefreshOwner && !isRetryRequest) {
          const badRefreshTokenMessage = {
            clearLocalDatabase: false,
            isExpiredVersion: false,
          };
          try {
            const response: any = { ...data };
            const error: ServerError = response.error;
            if (response && response.clearLocalDatabase) {
              badRefreshTokenMessage.clearLocalDatabase = response.clearLocalDatabase;
            }
            if (response && error && error.code) {
              badRefreshTokenMessage.isExpiredVersion = error.code === SERVER_RESPONSE_ERRORS.VERSION_IS_EXPIRED;
            }
          } catch (err) {}
          this.badRefreshToken.next(badRefreshTokenMessage);
        }
      }
      throw data;
    } finally {
      if (isRefreshOwner) {
        this.pendingRefresh = null;
      }
    }
  }

  public doSecureRequestObservable(
    url: string,
    type: 'get' | 'post' | 'delete' | 'put',
    data?: Object,
    optionsData?: ISecureRequestOptions
  ): Observable<SecuredResponse> {
    return new Observable((observer) => {
      this.doSecureRequest(url, type, data, optionsData)
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  public resetCompany() {
    const removedTypesPromises = Object.values(UPDATES_TYPES).map((value) => this.DbDaoService.deleteDatabase(value.type));
    return Promise.all(removedTypesPromises);
  }

  public checkLicenseExpiration(licenseInfo: License): boolean {
    if (!licenseInfo.isActive) {
      const [, url] = PAYMASH_PROFILE.SERVER_URL.split('//');
      this.showError('license_expired_title', 'license_expired_description', { url });
      return false;
    }
    if (!licenseInfo.isPosActivated) {
      this.showError('license_pos_not_included_title', 'license_pos_not_included_message');
      return false;
    }
    if (licenseInfo.isNumberOfConcurrentUsersExceeded) {
      this.showError('license_warning_title', 'license_warning_description');
      return true;
    }
    return true;
  }

  public async showError(titleKey: string, messageKey: string, messageProps: Object = {}) {
    if (!this.errorAlert) {
      this.errorAlert = await this.AlertService.create({
        header: this.TranslateService.instant(titleKey),
        subHeader: this.TranslateService.instant(messageKey, messageProps),
        buttons: ['OK'],
      });
      this.errorAlert
        .onDidDismiss()
        .then(() => (this.errorAlert = null))
        .catch((err) => this.logger.error(err, 'showError:onDidDismiss'));

      this.errorAlert.present().catch((err) => this.logger.error(err, 'showError:present'));
    }
  }

  public logout() {
    this.logoutEvent.next();
  }

  public observableCompanyProperties(...properties: CompanyProperties[]): Observable<Company> {
    return this.loggedCompanyData.pipe(
      pairwise(),
      filter((pairs) => {
        if (pairs[0] && pairs[1] && !!properties?.length) {
          return properties.some((property) => pairs[0][property] !== pairs[1][property]);
        }
        return false;
      }),
      map((pairs) => pairs[1])
    );
  }

  private createRequestAsPromise(
    url: string,
    type: string,
    options: any,
    data?: Object,
    requestTimeout?: number
  ): Promise<HttpResponse<any>> {
    requestTimeout = requestTimeout || PAYMASH_PROFILE.requestTimeout.max;
    const request: Observable<HttpResponse<any>> = data ? this.http[type](url, data, options) : this.http[type](url, options);
    return firstValueFrom(request.pipe(timeout(requestTimeout)));
  }

  private createTimeoutErrorResponse(url: string) {
    return new HttpResponse({
      status: 408,
      body: 'Request Timeout',
      url,
    });
  }

  private getFingerprint() {
    return FingerprintPlugin.getFingerprint('Unique').then((result) => {
      this.logger.debug('getFingerprint', { result });
      return result.fingerprint;
    });
  }
}
